/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { AnalyticsEvent } from "@packages/analytics";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useFavoriteCollectionAction } from "@packages/kids";
import { useAddToQueue } from "@packages/media";
import type {
  Collection,
  CollectionSectionItem,
  SectionItem,
  TrustedCollection,
} from "@packages/sdk";
import { useBranch, useRequestFavorites } from "@packages/sdk";
import {
  cssMediaQueryMinWidthTablet,
  HideFromKids,
  PlayAllIcon,
  PlayLastIcon,
  PlayNextIcon,
  QuickAction,
  ShareIcon,
  ShareModal,
  spacing,
  StarIcon,
  useModal,
} from "@packages/ui";
import { useMemo } from "react";

const quickActionsBarStyles: Interpolation = {
  display: "flex",
  flexDirection: "row",
  gap: spacing.s,
  justifyContent: "center",
  padding: `${spacing.m} ${spacing.none}`,
  [cssMediaQueryMinWidthTablet]: {
    padding: `${spacing.none}`,
  },
};

type Props = {
  collection: TrustedCollection | Collection;
  color: string;
  id: number;
  image: string;
  playAll: () => void;
  sessionsAvailable: boolean;
  text: string;
};

export const CollectionQuickActions = ({
  collection,
  color,
  id,
  image,
  playAll,
  sessionsAvailable,
  text,
}: Props) => {
  const analytics = useAnalytics();
  const { action: updateFavorite, isPending: isFavoritePending } =
    useFavoriteCollectionAction({
      id,
      has_access: collection?.has_access ?? true,
      title: collection?.title,
      images: collection?.images,
    });
  const { query: favoritesQuery } = useRequestFavorites({
    displayType: "collections",
  });
  const t = useTranslations();
  const shareModal = useModal(ShareModal);
  const branch = useBranch();
  const { handleClickNext, handleClickLast, stringKeyLast, stringKeyNext } =
    useAddToQueue({
      type: collection.is_album ? "collection" : "collection-next",
      id,
    });

  const favoriteCollectionItem = favoritesQuery.data?.items?.find(
    (item: SectionItem) => item.item_id === id,
  ) as CollectionSectionItem | undefined;
  const favoritedSessionCount =
    favoriteCollectionItem?.reference?.prayers_count ?? 0;

  const availablePrayersCount = useMemo(
    () => collection.prayers.filter((prayer) => prayer.is_available).length,
    [collection.prayers],
  );

  const isFullyFavorited =
    !!favoriteCollectionItem &&
    !!availablePrayersCount &&
    favoritedSessionCount >= availablePrayersCount;

  const handleFavorite = () => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedCollectionOption,
      properties: {
        collection: id,
        option: isFullyFavorited ? "unfavorite" : "favorite",
      },
    });

    updateFavorite();
  };

  const handlePlayAll = () => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedPlayAll,
      properties: {
        collection: id,
      },
    });
    playAll();
  };

  const handleShare = () => {
    if (collection.type === "challenge")
      analytics.track({
        event: HallowAnalyticsEvent.TappedShareChallenge,
        properties: {
          challenge: id,
          collection: id,
        },
      });
    else
      analytics.track({
        event: HallowAnalyticsEvent.TappedShareCollection,
        properties: {
          collection: id,
        },
      });
  };

  const shareAnalyticsEvent: AnalyticsEvent =
    collection.type === "challenge"
      ? {
          event: HallowAnalyticsEvent.ViewedShareChallengeDialog,
          properties: {
            challenge: id,
            collection: id,
          },
        }
      : {
          event: HallowAnalyticsEvent.ViewedShareCollectionDialog,
          properties: {
            collection: id,
          },
        };

  return (
    <div css={quickActionsBarStyles}>
      <QuickAction
        disabled={!sessionsAvailable}
        icon={<PlayAllIcon />}
        onClickCapture={handlePlayAll}
      >
        {t("chapter_details_play_all")}
      </QuickAction>
      <QuickAction
        disabled={isFavoritePending}
        icon={isFullyFavorited ? <StarIcon.Off /> : <StarIcon.Outline />}
        onClick={() => handleFavorite()}
      >
        {t(
          isFullyFavorited
            ? "dialog_prayer_options_unfavorite"
            : "dialog_prayer_options_favorite",
        )}
      </QuickAction>
      <HideFromKids>
        <QuickAction
          icon={<ShareIcon />}
          onPointerDown={handleShare}
          onClick={async () =>
            shareModal.show({
              analyticsEvent: shareAnalyticsEvent,
              color,
              imgSrc: image,
              title: text,
              link: await branch.collection({ id }),
            })
          }
        >
          {t("dialog_prayer_options_share")}
        </QuickAction>
      </HideFromKids>

      {collection.is_album && collection.has_access ? (
        <>
          <QuickAction icon={<PlayNextIcon />} onClick={handleClickNext}>
            {t(stringKeyNext)}
          </QuickAction>

          <QuickAction icon={<PlayLastIcon />} onClick={handleClickLast}>
            {t(stringKeyLast)}
          </QuickAction>
        </>
      ) : null}
    </div>
  );
};
